import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { SidebarHeader } from "@/components/sidebar/components/SidebarHeader.tsx";
import { useEmployeeMe } from "@/queries/employees/use-employees.query";
import { useGoalsCounts } from "@/queries/goals.query";
import { Avatar, FontAwesomeIcon, SidebarPanelItem, Stack, Title } from "@skailer-com/uikit";
import { ComponentProps, Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

interface SideBarGoalsItem {
  titleGroup?: string;
  children: ComponentProps<typeof SidebarPanelItem>[];
}

export const SidebarGoals = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { data: counts } = useGoalsCounts();
  const { data: employee } = useEmployeeMe();

  const menu = useMemo<SideBarGoalsItem[]>(() => {
    const departmentsWithGoals = (counts?.departments ?? []).filter(
      (departmentItem) => departmentItem.count > 0
    );
    const teamsWithGoals = (counts?.teams ?? []).filter((teamItem) => teamItem.count > 0);

    const sectionGroups: ComponentProps<typeof SidebarPanelItem>[] = [];

    departmentsWithGoals.forEach((departmentItem) => {
      const department = departmentItem.department;
      sectionGroups.push({
        title: department.title,
        to: `/goals/department/${department.id}`,
        isActive: pathname === `/goals/department/${department.id}`,
        icon: (
          <Avatar
            variant={"default"}
            size={"sm"}
            acronym={department.title}
            url={department.image_url ?? undefined}
            emoji={department.emoji ?? undefined}
          />
        ),
        count: departmentItem.count,
      });
    });

    teamsWithGoals.forEach((teamItem) => {
      const team = teamItem.team;
      sectionGroups.push({
        title: team.title,
        to: `/goals/team/${team.id}`,
        isActive:
          pathname === `/goals/team/${team.id}` || pathname.startsWith(`/goals/team/${team.id}`),
        icon: <Avatar variant={"default"} size={"sm"} acronym={team.title} emoji={team.emoji} />,
        count: teamItem.count,
      });
    });

    if ((counts?.direct_reports?.count ?? 0) > 0) {
      sectionGroups.push({
        title: t("core:direct_reports"),
        to: "/goals/reports/direct",
        isActive:
          pathname === "/goals/reports/direct" || pathname.startsWith("/goals/reports/direct"),
        icon: <FontAwesomeIcon icon="fa-light fa-diagram-nested" />,
        count: counts?.direct_reports?.count,
      });
    }

    if ((counts?.subordinates?.count ?? 0) > 0) {
      sectionGroups.push({
        title: t("core:all_subordinates"),
        to: "/goals/reports/subordinates",
        isActive:
          pathname === "/goals/reports/subordinates" ||
          pathname.startsWith("/goals/reports/subordinates"),
        icon: <FontAwesomeIcon icon="fa-light fa-chart-network" />,
        count: counts?.subordinates?.count,
      });
    }

    const menuConfig: SideBarGoalsItem[] = [
      {
        children: [
          {
            title: t("goals:my_goals"),
            to: "/goals/personal",
            isActive: pathname === "/goals/personal" || pathname.startsWith("/goals/personal"),
            icon: employee ? (
              <EmployeeAvatar variant={"plain"} employee={employee} size={"sm"} />
            ) : undefined,
            subtitleSlot: counts?.personal.count,
          },
        ],
      },
      {
        titleGroup: t("goals:explore"),
        children: [
          {
            title: t("goals:all_goals"),
            to: "/goals/search",
            isActive: pathname === "/goals/search" || pathname.startsWith("/goals/search"),
            icon: <FontAwesomeIcon icon="fa-bullseye fa-solid" />,
          },
          {
            title: t("goals:strategic_goals"),
            to: "/goals/strategic",
            isActive: pathname === "/goals/strategic" || pathname.startsWith("/goals/strategic"),
            icon: <FontAwesomeIcon icon="fa-light fa-chess" />,
          },
          {
            title: t("goals:initiatives_goals"),
            to: "/goals/initiatives",
            isActive:
              pathname === "/goals/initiatives" || pathname.startsWith("/goals/initiatives"),
            icon: <FontAwesomeIcon icon="fa-light fa-hand-wave" />,
          },
        ],
      },
    ];

    if (sectionGroups.length > 0) {
      menuConfig.push({ titleGroup: t("core:groups"), children: sectionGroups });
    }

    return menuConfig;
  }, [counts, employee, pathname, t]);

  return (
    <>
      <SidebarHeader title={t("goals:goals")} />
      <Stack className={"px-4"} gap={"xs"}>
        {menu.map(({ children, titleGroup }, index) => (
          <Fragment key={`goals-group-${index}`}>
            {titleGroup && (
              <Title header={titleGroup} variant="secondary" size="xs" className="ml-2 mt-4" />
            )}
            {children.map((item) => (
              <SidebarPanelItem
                key={item.to}
                to={item.to}
                isActive={item.isActive}
                title={item.title}
                icon={item.icon}
                count={item.count}
                subtitleSlot={item.subtitleSlot}
              />
            ))}
          </Fragment>
        ))}
      </Stack>
    </>
  );
};
